import React, {useCallback, useMemo} from 'react';
import {classes, style} from './doppe-action-type-contact-card-list-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeCommonListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-common-list-view-item/doppe-action-type-common-list-view-item';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueGetValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppeHideableValueToggleHidden
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    asyncExecuteInBackground,
    evaluateFunction,
    iterableMapToArray,
    ObservableValue
} from '@wix/devzai-utils-common';
import {DoppeActionTypeContactCardList} from '../doppe-action-type-contact-card-list';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {doppeActionFullNameGetName} from '../../../client-server-common/types/doppe-action-full-name';
import {
    DoppeActionTypeContactCardWidgetInner
} from '../../doppe-action-type-contact-card/doppe-action-type-contact-card-widget/doppe-action-type-contact-card-widget';
import {BymoPageLayout} from '../../../components/bymo-page-style/bymo-page-style';
import {DoppeViewerModalPanelLayout} from '../../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {
    DoppeViewerModalOpeningAnimation
} from '../../../components/doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import {
    DoppeViewerContactMethodActionsPanel
} from '../../../components/doppe-viewer-contact-method-action-panel/doppe-viewer-contact-method-action-panel';
import {
    doppeViewerActionHandleListItemClick
} from '../../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {
    useAssertWidgetRenderingContext
} from '../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {
    doppeActionLayoutMediaRatioResolveWidthHeightRatio,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemCalculateItemSections,
    doppeListActionSettingsFilterDeletedListItemsFromActionView,
    doppeListActionSettingsFilterHiddenListItemsFromActionView
} from '../../../client-server-common';
import {
    doppeDtoContactDetailsDownloadContactCard
} from "../../../client-app-and-viewer-common/doppe-dto-contact-details-client/doppe-dto-contact-details-client";

export const DoppeActionTypeContactCardListWidget = React.memo(function DoppeActionTypeContactCardListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeContactCardList.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        observableCurrentItemIdToRender
    } = useAssertWidgetRenderingContext();

    const actualObservableCurrentItemIdToRender = evaluateFunction(() => {
        return observableCurrentItemIdToRender ? observableCurrentItemIdToRender : new ObservableValue<string | null>(null);
    })


    const {
        listDataItems,
        layout,
        mediaSize,
        mediaRatio,

        previewShowDescription,
        previewShowTitle,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
        itemShowTextOnImage,
    } = actionSettings;

    const expandedContactId = useObservableValue(actualObservableCurrentItemIdToRender);

    const currentContactToShow = useMemo(
        () => listDataItems.find(contact => contact.id === expandedContactId) ?? null,
        [listDataItems, expandedContactId]);


    const existingContacts = useMemo(() => {
        return iterableMapToArray(doppeListActionSettingsFilterHiddenListItemsFromActionView(listDataItems), (contact) => {
            return {
                ...contact,
                businessEntityDto: undefined
            }
        })
    }, [listDataItems]);

    const contactsListViewDataSource = useMemo(() => {
        return listViewCreateDataSource(existingContacts, {
            idFunction: item => item.id
        });
    }, [existingContacts]);

    const saveAsContact = useCallback(() => {
        if (currentContactToShow) {
            asyncExecuteInBackground(async () => {
                await doppeDtoContactDetailsDownloadContactCard({
                    media: doppeHideableValueGetVisibleValue(currentContactToShow.saveContactProfileImage, null) ?? currentContactToShow.media?.[0] ?? null,
                    name: doppeHideableValueGetVisibleValue(currentContactToShow.name, null),
                    company: doppeHideableValueGetVisibleValue(currentContactToShow.company, ''),
                    notes: currentContactToShow.notes,
                    jobTitle: doppeHideableValueGetVisibleValue(currentContactToShow.jobTitle, ''),
                    contactMethods: doppeListActionSettingsFilterDeletedListItemsFromActionView(currentContactToShow.contactMethods),
                })
            }, {});
        }

    }, [currentContactToShow])

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            {evaluateFunction(() => {
                if (!currentContactToShow) {
                    if (contactsListViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return (
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                actionContext={actionContext}
                                layout={layout}
                                mediaSize={mediaSize}
                                listTitle={actionSettings.mainTitle}
                                media={doppeHideableValueGetVisibleValue(actionSettings.mainImage, undefined)}
                                mediaViewSettings={actionSettings.mainMediaViewSettings}
                                mediaViewLayout={actionSettings.mainMediaViewLayout}
                                onItemClick={contactData => {
                                    doppeViewerActionHandleListItemClick(actionContext, {
                                        itemId: contactData.id,
                                        itemIndex: existingContacts.findIndex(item => item.id === contactData.id),
                                    })
                                }}
                                renderItem={(dataItem, layout, index) => {
                                    const {
                                        media,
                                        name,
                                        description,
                                        jobTitle,
                                        // businessEntityDto,
                                    } = dataItem;

                                    return (
                                        <DoppeActionTypeCommonListViewItem
                                            mediaSize={mediaSize}
                                            actionContext={actionContext}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            title={doppeActionFullNameGetName(name)}
                                            description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                            image={media?.[0] ?? null}
                                            layout={layout}
                                            gridImageRatio={doppeActionLayoutMediaRatioResolveWidthHeightRatio(mediaRatio)}
                                            isOneItem={contactsListViewDataSource.length === 1}
                                            textOnImage={itemShowTextOnImage && doppeHideableValueIsVisibleAndNotEqualValue(jobTitle, '') ? doppeHideableValueGetValue(jobTitle) : undefined}
                                            hideImageWhenPossible={!itemShowImage}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={contactsListViewDataSource}
                            />
                        )
                    }
                } else {
                    return (
                        <DoppeActionTypeContactCardWidgetInner
                            actionContext={actionContext}
                            actionSettings={{
                                media: currentContactToShow.media,
                                contactMethods: currentContactToShow.contactMethods,
                                description: doppeHideableValueToggleHidden(
                                    currentContactToShow.description,
                                    isHidden => isHidden ? true : !previewShowDescription
                                ),
                                name: doppeHideableValueToggleHidden(
                                    currentContactToShow.name,
                                    isHidden => isHidden ? true : !previewShowTitle
                                ),
                                notes: currentContactToShow.notes,
                                company: currentContactToShow.company,
                                jobTitle: currentContactToShow.jobTitle,
                                saveContactProfileImage: doppeHideableValueCreateHidden(null),
                                ...doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, currentContactToShow),
                                ...doppeListActionListItemCalculateItemSections(actionSettings.listItemTemplateSectionsSettings, currentContactToShow),
                            }}
                            listItemId={currentContactToShow.id}
                            reportGoalAchieved={() => {
                                actionContext.reportGoalAchieved({
                                    goalType: DoppeActionGoalType.Click,
                                    innerItemId: currentContactToShow?.id,
                                    innerItemPosition: existingContacts.findIndex(item => item.id === currentContactToShow?.id)
                                });
                            }}
                            reportMethodUsed={(methodId, index) => {
                                actionContext.reportGoalAchieved({
                                    goalType: DoppeActionGoalType.Click,
                                    innerItemId: currentContactToShow?.id,
                                    innerItemPosition: existingContacts.findIndex(item => item.id === currentContactToShow?.id)
                                });

                                actionContext.reportOnlyInnerItemClicked({
                                    innerItemId: methodId,
                                    innerItemPosition: index,
                                    goalType: DoppeActionGoalType.Click
                                });
                            }}
                            reportMethodViewed={(methodId, index) => {
                                actionContext.reportInnerItemViewed(methodId, index);
                            }}
                            actionId={actionContext.action.id}
                            saveAsContact={saveAsContact}
                            openContactMethodAction={(method) => {
                                actionContext.modalsController.showInnerModal({
                                    openingAnimation: bymoPageLayout => {
                                        const modalPanelLayout = bymoPageLayout === BymoPageLayout.Wide ?
                                            DoppeViewerModalPanelLayout.Inner :
                                            DoppeViewerModalPanelLayout.Drawer;

                                        switch (modalPanelLayout) {
                                            case DoppeViewerModalPanelLayout.Drawer: {
                                                return DoppeViewerModalOpeningAnimation.SlideInFromBottom;
                                            }
                                            default: {
                                                return DoppeViewerModalOpeningAnimation.FadeIn;
                                            }
                                        }
                                    },
                                    content: <DoppeViewerContactMethodActionsPanel
                                        title={doppeHideableValueGetVisibleValue(method.title, '')}
                                        type={method.type}
                                        displayValue={method.displayValue}
                                        onClick={() => {
                                            actionContext.reportGoalAchieved({
                                                goalType: DoppeActionGoalType.Click,
                                                innerItemId: currentContactToShow?.id,
                                                innerItemPosition: existingContacts.findIndex(item => item.id === currentContactToShow?.id)
                                            });

                                            actionContext.reportOnlyInnerItemClicked({
                                                innerItemId: method.id,
                                                innerItemPosition: currentContactToShow?.contactMethods ? doppeListActionSettingsFilterHiddenListItemsFromActionView(currentContactToShow?.contactMethods).findIndex(item => item.id === method.id) : -1,
                                                goalType: DoppeActionGoalType.Click
                                            });
                                        }}
                                        showNotification={(text: string) => {
                                            actionContext.showNotification({
                                                message: text
                                            })
                                        }}
                                        saveAsContact={saveAsContact}
                                    />
                                });
                            }}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});


